import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "../AuthContext";
import { useAlert } from "../AlertContext";
import { getCustomerDetail, updateCustomer } from "../api";
import { formatDateTimeLocalInput, normalizeDateTimeInput } from "../utils";
import { hasDocumentedConsent, validateConsentMetadata } from "../consent";

const APPOINTMENT_STATUS_META = {
  REQUESTED: { label: "Angefragt", color: "default" },
  CONFIRMED: { label: "Bestätigt", color: "success" },
  RESCHEDULED: { label: "Verschoben", color: "warning" },
  CANCELLED: { label: "Storniert", color: "error" },
};

const WORK_ORDER_STATUS_META = {
  OPEN: { label: "Offen", color: "info" },
  IN_PROGRESS: { label: "In Arbeit", color: "warning" },
  DONE: { label: "Abgeschlossen", color: "success" },
  CANCELLED: { label: "Storniert", color: "error" },
};

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return date.toLocaleString("de-DE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function formatAppointmentSlot(appointment) {
  const { date, time } = appointment;
  if (!date && !time) {
    return "—";
  }
  if (date && time) {
    const dt = new Date(`${date}T${time}`);
    if (!Number.isNaN(dt.getTime())) {
      return dt.toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
    }
  }
  if (date) {
    const d = new Date(date);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString("de-DE");
    }
  }
  return time ?? "—";
}

const EMPTY_FORM = {
  salutation: "",
  firstName: "",
  lastName: "",
  company: "",
  email: "",
  phone: "",
  street: "",
  zip: "",
  city: "",
  country: "",
  notes: "",
  consentGiven: false,
  consentSource: "",
  consentTimestamp: "",
  consentText: "",
};

export default function CustomerDetailPage() {
  const { id } = useParams();
  const { token, roles, hasRole } = useContext(AuthContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showAlert } = useAlert();

  const assignedRoles = Array.isArray(roles) ? roles : roles ? [roles] : [];
  const canEdit =
    (typeof hasRole === "function" && (hasRole("ADMIN") || hasRole("DISPONENT"))) ||
    assignedRoles.includes("ADMIN") ||
    assignedRoles.includes("DISPONENT");
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["customerDetail", id],
    queryFn: () => getCustomerDetail(token, id),
    enabled: Boolean(token && id),
  });

  useEffect(() => {
    if (!data?.customer) {
      return;
    }
    const customer = data.customer;
    setFormValues({
      salutation: customer.salutation ?? "",
      firstName: customer.firstName ?? "",
      lastName: customer.lastName ?? "",
      company: customer.company ?? "",
      email: customer.email ?? "",
      phone: customer.phone ?? "",
      street: customer.street ?? "",
      zip: customer.zip ?? "",
      city: customer.city ?? "",
      country: customer.country ?? "",
      notes: customer.notes ?? "",
      consentGiven: Boolean(customer.consentGiven),
      consentSource: customer.consentSource ?? "",
      consentTimestamp: formatDateTimeLocalInput(customer.consentTimestamp),
      consentText: customer.consentText ?? "",
    });
    setFormErrors({});
    setSubmitError(null);
  }, [data]);

  const mutation = useMutation({
    mutationFn: (payload) => updateCustomer(token, id, payload),
    onSuccess: async () => {
      setFormErrors({});
      setSubmitError(null);
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      await refetch();
      showAlert("Kundendaten wurden aktualisiert.");
    },
    onError: (err) => {
      const fieldErrors = {};
      let nonFieldError = err.message;
      if (Array.isArray(err.validationErrors)) {
        err.validationErrors.forEach((item) => {
          if (item.field && item.field !== "customerRequest") {
            fieldErrors[item.field] = item.message;
          } else if (item.message) {
            nonFieldError = item.message;
          }
        });
      }
      setFormErrors(fieldErrors);
      setSubmitError(nonFieldError);
    },
  });

  const isSaving = canEdit ? mutation.isPending : false;

  const nameRequirementError = useMemo(() => {
    const lastName = formValues.lastName?.trim();
    const company = formValues.company?.trim();
    return !lastName && !company;
  }, [formValues.lastName, formValues.company]);

  const handleChange = (field) => (event) => {
    setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleConsentToggle = (event) => {
    const checked = event.target.checked;
    setFormValues((prev) => ({ ...prev, consentGiven: checked }));
    if (!checked) {
      setFormErrors((prev) => ({
        ...prev,
        consentSource: undefined,
        consentTimestamp: undefined,
        consentText: undefined,
      }));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canEdit) {
      return;
    }
    setSubmitError(null);
    setFormErrors({});
    const nextErrors = validateConsentMetadata(formValues);
    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      setSubmitError("Bitte vervollständigen Sie alle Pflichtfelder.");
      return;
    }
    const payload = {
      ...formValues,
      consentGiven: formValues.consentGiven,
      consentSource: formValues.consentGiven ? formValues.consentSource || "" : "",
      consentTimestamp: formValues.consentGiven
        ? normalizeDateTimeInput(formValues.consentTimestamp)
        : null,
      consentText: formValues.consentGiven ? formValues.consentText || "" : "",
    };
    mutation.mutate(payload);
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Stack spacing={2}>
          <Button startIcon={<ArrowBackIcon />} variant="outlined" onClick={() => navigate(-1)}>
            Zurück
          </Button>
          <Alert severity="error">{error.message || "Kunde konnte nicht geladen werden."}</Alert>
        </Stack>
      </Container>
    );
  }

  const customer = data?.customer;
  const hasPersistedConsent = hasDocumentedConsent(customer);
  const effectiveConsent = formValues.consentGiven;
  const consentTooltip = hasPersistedConsent
    ? `Einwilligung dokumentiert: ${formatDateTime(customer?.consentTimestamp)}`
    : "Keine dokumentierte Einwilligung vorhanden.";
  const appointments = data?.appointments ?? [];
  const workOrders = data?.workOrders ?? [];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button startIcon={<ArrowBackIcon />} variant="outlined" onClick={() => navigate(-1)}>
            Zurück
          </Button>
          <Typography variant="h4" component="h1">
            Kundenstammdaten
          </Typography>
          <Tooltip title={consentTooltip}>
            <Chip
              label={hasPersistedConsent ? "Einwilligung dokumentiert" : "Einwilligung fehlt"}
              color={hasPersistedConsent ? "success" : "default"}
              variant={hasPersistedConsent ? "filled" : "outlined"}
            />
          </Tooltip>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Letzte Aktivität: {formatDateTime(data?.lastActivity)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Einwilligung: {hasPersistedConsent ? "ja" : "nein"}
        </Typography>
        {hasPersistedConsent && (
          <Typography variant="body2" color="text.secondary">
            Quelle: {customer?.consentSource || "—"} · Zeitpunkt: {formatDateTime(customer?.consentTimestamp)}
          </Typography>
        )}
        {!hasPersistedConsent && (
          <Alert severity="warning">
            Für TecDoc-Anreicherungen ist eine dokumentierte Einwilligung erforderlich.
          </Alert>
        )}
        <Card component={canEdit ? "form" : "div"} onSubmit={canEdit ? handleSubmit : undefined}>
          <CardHeader title="Stammdaten" subheader={`Zuletzt aktualisiert: ${formatDateTime(data?.customer?.updatedAt)}`} />
          <CardContent>
            {!canEdit && (
              <Box sx={{ mb: 3 }}>
                <Alert severity="info">
                  Sie können Kundendaten nur lesen. Notizen sind für Ihre Rolle ausgeblendet.
                </Alert>
              </Box>
            )}
            {canEdit && effectiveConsent && !hasPersistedConsent && (
              <Box sx={{ mb: 3 }}>
                <Alert severity="info">
                  Speichern Sie die Einwilligung, bevor Sie TecDoc-Daten anreichern.
                </Alert>
              </Box>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  label="Anrede"
                  value={formValues.salutation}
                  onChange={handleChange("salutation")}
                  fullWidth
                  disabled={!canEdit}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  label="Vorname"
                  value={formValues.firstName}
                  onChange={handleChange("firstName")}
                  fullWidth
                  disabled={!canEdit}
                  error={canEdit && Boolean(formErrors.firstName)}
                  helperText={canEdit ? formErrors.firstName || "" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  label="Nachname"
                  value={formValues.lastName}
                  onChange={handleChange("lastName")}
                  fullWidth
                  disabled={!canEdit}
                  error={canEdit && (Boolean(formErrors.lastName) || nameRequirementError)}
                  helperText={
                    canEdit
                      ? formErrors.lastName || (nameRequirementError ? "Nachname oder Firma erforderlich" : "")
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Firma"
                  value={formValues.company}
                  onChange={handleChange("company")}
                  fullWidth
                  disabled={!canEdit}
                  error={canEdit && (Boolean(formErrors.company) || nameRequirementError)}
                  helperText={
                    canEdit
                      ? formErrors.company || (nameRequirementError ? "Nachname oder Firma erforderlich" : "")
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="E-Mail"
                  value={formValues.email}
                  onChange={handleChange("email")}
                  fullWidth
                  type="email"
                  disabled={!canEdit}
                  error={canEdit && Boolean(formErrors.email)}
                  helperText={canEdit ? formErrors.email || "" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Telefon"
                  value={formValues.phone}
                  onChange={handleChange("phone")}
                  fullWidth
                  disabled={!canEdit}
                  error={canEdit && Boolean(formErrors.phone)}
                  helperText={canEdit ? formErrors.phone || "" : ""}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Straße"
                  value={formValues.street}
                  onChange={handleChange("street")}
                  fullWidth
                  disabled={!canEdit}
                  error={canEdit && Boolean(formErrors.street)}
                  helperText={canEdit ? formErrors.street || "" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <TextField
                  label="PLZ"
                  value={formValues.zip}
                  onChange={handleChange("zip")}
                  fullWidth
                  disabled={!canEdit}
                  error={canEdit && Boolean(formErrors.zip)}
                  helperText={canEdit ? formErrors.zip || "" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={8} md={4}>
                <TextField
                  label="Ort"
                  value={formValues.city}
                  onChange={handleChange("city")}
                  fullWidth
                  disabled={!canEdit}
                  error={canEdit && Boolean(formErrors.city)}
                  helperText={canEdit ? formErrors.city || "" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Land"
                  value={formValues.country}
                  onChange={handleChange("country")}
                  fullWidth
                  disabled={!canEdit}
                  error={canEdit && Boolean(formErrors.country)}
                  helperText={canEdit ? formErrors.country || "" : ""}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notizen"
                  value={canEdit ? formValues.notes : ""}
                  onChange={handleChange("notes")}
                  fullWidth
                  multiline
                  minRows={3}
                  disabled={!canEdit}
                  error={canEdit && Boolean(formErrors.notes)}
                  helperText={
                    canEdit ? formErrors.notes || "" : "Notizen sind für Ihre Rolle ausgeblendet."
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formValues.consentGiven}
                      onChange={handleConsentToggle}
                      disabled={!canEdit}
                    />
                  }
                  label="Einwilligung zur TecDoc-Datenverarbeitung dokumentiert"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Quelle der Einwilligung"
                  value={formValues.consentSource}
                  onChange={handleChange("consentSource")}
                  fullWidth
                  disabled={!canEdit || !formValues.consentGiven}
                  error={canEdit && Boolean(formErrors.consentSource)}
                  helperText={canEdit ? formErrors.consentSource || "" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Zeitpunkt der Einwilligung"
                  type="datetime-local"
                  value={formValues.consentTimestamp}
                  onChange={handleChange("consentTimestamp")}
                  fullWidth
                  disabled={!canEdit || !formValues.consentGiven}
                  InputLabelProps={{ shrink: true }}
                  error={canEdit && Boolean(formErrors.consentTimestamp)}
                  helperText={canEdit ? formErrors.consentTimestamp || "" : ""}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Textbaustein"
                  value={formValues.consentText}
                  onChange={handleChange("consentText")}
                  fullWidth
                  multiline
                  minRows={2}
                  disabled={!canEdit || !formValues.consentGiven}
                  error={canEdit && Boolean(formErrors.consentText)}
                  helperText={canEdit ? formErrors.consentText || "" : ""}
                />
              </Grid>
            </Grid>
            {canEdit && submitError && (
              <Box sx={{ mt: 3 }}>
                <Alert severity="error">{submitError}</Alert>
              </Box>
            )}
          </CardContent>
          {canEdit && (
            <CardActions sx={{ justifyContent: "flex-end", px: 3, pb: 3 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                disabled={isSaving || nameRequirementError}
              >
                {isSaving ? "Speichern…" : "Speichern"}
              </Button>
            </CardActions>
          )}
        </Card>

        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6">Termine</Typography>
            {appointments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Keine Termine vorhanden.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Slot</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>Erstellt</TableCell>
                    <TableCell>Aktualisiert</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {appointments.map((appointment) => {
                    const meta = APPOINTMENT_STATUS_META[appointment.status] ?? {
                      label: appointment.status,
                      color: "default",
                    };
                    return (
                      <TableRow key={appointment.id}>
                        <TableCell>{formatAppointmentSlot(appointment)}</TableCell>
                        <TableCell>
                          <Chip label={meta.label} color={meta.color} size="small" />
                        </TableCell>
                        <TableCell>{appointment.serviceName || "—"}</TableCell>
                        <TableCell>{formatDateTime(appointment.createdAt)}</TableCell>
                        <TableCell>{formatDateTime(appointment.updatedAt)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Stack>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6">Aufträge</Typography>
            {workOrders.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Keine Aufträge vorhanden.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Beschreibung</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Termin</TableCell>
                    <TableCell>Erstellt</TableCell>
                    <TableCell>Aktualisiert</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workOrders.map((order) => {
                    const meta = WORK_ORDER_STATUS_META[order.status] ?? {
                      label: order.status,
                      color: "default",
                    };
                    return (
                      <TableRow key={order.id}>
                        <TableCell>{order.description || "—"}</TableCell>
                        <TableCell>
                          <Chip label={meta.label} color={meta.color} size="small" />
                        </TableCell>
                        <TableCell>{order.appointmentId ? `#${order.appointmentId}` : "—"}</TableCell>
                        <TableCell>{formatDateTime(order.createdAt)}</TableCell>
                        <TableCell>{formatDateTime(order.updatedAt)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}
